import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Printer, LogOut, MessageCircle } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { API, clearCode, errMsg, waDoubt, WA_SESSION, getDeviceId } from "@/lib/api";
import { usePack } from "@/hooks/usePack";
import { DayStrip } from "@/components/pack/DayStrip";
import { DayCard } from "@/components/pack/DayCard";
import { Celebrate } from "@/components/pack/Celebrate";
import PartnerModule from "@/components/pack/PartnerModule";
import { Logo } from "@/components/Logo";

const SupportCard = ({ pack, day }) => {
  if (!pack.support_enabled) return null;
  const until = new Date(pack.support_until);
  const active = until > new Date();
  const date = until.toLocaleDateString("es-ES", { day: "numeric", month: "long" });
  return (
    <section data-testid="support-card" className="mt-8 rounded-lg bg-[var(--forest)] text-[var(--cream)] p-6 sm:p-8 flex flex-col md:flex-row md:items-center gap-5">
      <div className="w-12 h-12 rounded-lg grid place-items-center bg-[var(--sage)] text-[var(--charcoal)] shrink-0"><MessageCircle /></div>
      <div className="flex-1">
        <p className="font-display text-2xl">Acompañamiento educativo por WhatsApp</p>
        <p className="text-white/80 mt-1" data-testid="support-status">
          {active ? `Hasta el ${date} puedes enviarnos dudas sobre las lecciones y cómo practicar los ejercicios. El equipo responde personalmente por texto o audio en un máximo de 48 h laborables. Es orientación psicoeducativa, no terapia individual ni atención de urgencias.` : `Tu acompañamiento educativo por WhatsApp terminó el ${date}. Si quieres una atención individual, puedes reservar una sesión privada por videollamada con Cristina Artamendi, psicóloga sanitaria.`}
        </p>
      </div>
      <a data-testid="support-whatsapp-button" href={active ? waDoubt(pack.buyer, pack.code, day) : WA_SESSION} target="_blank" rel="noreferrer" className="btn-accent"
        onClick={() => active && toast("Cuando envíes tu duda, te llegará un aviso de que la hemos recibido. Te respondemos en un máximo de 48 h laborables.", { duration: 8000 })}>
        <MessageCircle size={18} /> {active ? `Preguntar sobre el día ${day}` : "Reservar sesión"}
      </a>
    </section>
  );
};

export default function Pack() {
  const [pack, setPack] = usePack();
  const [sel, setSelState] = useState(() => Number(sessionStorage.getItem("alania_sel")) || null);
  const setSel = (n) => { sessionStorage.setItem("alania_sel", n); setSelState(n); };
  const [party, setParty] = useState(null);
  const topRef = useRef(null);
  const nav = useNavigate();
  if (!pack) return <p className="p-10" data-testid="pack-loading">Cargando tu pack…</p>;

  const done = new Set(pack.completed);
  const totalDays = pack.days.length;
  const next = pack.days.find((d) => !done.has(d.day))?.day ?? totalDays;
  const current = sel ?? next;
  const day = pack.days.find((d) => d.day === current);
  const dayVideo = pack.videos.find((video) => video.day === current);
  const go = (n) => { setSel(n); setParty(null); topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); };

  const toggle = async () => {
    const marking = !done.has(current);
    try {
      const { data } = await axios.post(`${API}/pack/progress`, { code: pack.code, day: current, done: marking, device_id: getDeviceId() });
      setSel(current);
      setPack({ ...pack, completed: data.completed });
      if (marking) setParty(current);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const nextUndone = pack.days.find((d) => d.day > current && !pack.completed.includes(d.day))?.day ?? (current < totalDays ? current + 1 : null);

  return (
    <main data-testid="pack-page" className="px-5 sm:px-8 py-8 max-w-6xl mx-auto">
      <header className="flex items-center justify-between">
        <Link to="/" className="text-xl sm:text-2xl" data-testid="pack-logo"><Logo /></Link>
        <button data-testid="logout-button" onClick={() => { clearCode(); nav("/acceder"); }} className="text-sm inline-flex items-center gap-1.5 hover:underline"><LogOut size={16} /> Salir</button>
      </header>

      <section className="mt-10">
        <p className="font-hand text-3xl text-[var(--sage-deep)]">Hola, {pack.buyer}</p>
        <h1 className="font-display text-4xl sm:text-5xl leading-tight">{done.size === 0 ? `Hoy empieza ${pack.product_name}. Sin prisas.` : done.size === totalDays ? "¡Has completado el taller!" : `Llevas ${done.size} de ${totalDays} días. Sigue a tu ritmo.`}</h1>
        <div className="mt-6 max-w-xl">
          <div className="flex justify-between text-sm mb-2"><span data-testid="progress-count">{done.size} de {totalDays} días</span><span>{Math.round((done.size / totalDays) * 100)}%</span></div>
          <Progress value={(done.size / totalDays) * 100} className="h-2 bg-[var(--sand)]" />
        </div>
        <section data-testid="professional-orientation" className="mt-6 max-w-4xl rounded-lg border border-[var(--sand)] border-l-4 border-l-[var(--forest)] bg-white p-4 sm:p-5 md:grid md:grid-cols-[0.8fr_1.2fr] gap-5 items-center">
          <img src={pack.product_id === "rabietas-2-3" ? "/rabietas-guia-adulta.png" : "/equipo-en-calma-conversacion.png"}
            alt={pack.product_id === "rabietas-2-3" ? "Ilustración de un adulto acompañando con calma a un niño pequeño" : "Ilustración de una pareja conversando sobre un acuerdo familiar"}
            loading="lazy" decoding="async" className="w-full aspect-[16/9] rounded-md object-cover" />
          <div>
            <p className="font-semibold">Orientación psicológica · {pack.product_name}</p>
            <p className="mt-1 text-sm text-[#5a4f46]">Programa psicoeducativo elaborado por psicólogas graduadas por la UOC, con una psicóloga sanitaria reconocida en el equipo. Incluye ideas, ejemplos y actividades para practicar en el día a día.</p>
            <p className="mt-2 text-xs text-[#7a6c5e]">No sustituye terapia, diagnóstico ni atención psicológica individual. Si hay miedo, control o violencia, priorizad la seguridad y buscad apoyo especializado.</p>
          </div>
        </section>
        {done.size < totalDays && current !== next && (
          <button data-testid="continue-button" onClick={() => go(next)} className="btn-accent mt-6">Continúa por el día {next}</button>
        )}
        {done.size === totalDays && <p data-testid="completed-message" className="mt-6 text-lg text-[var(--sage-deep)]">Puedes volver a cualquier día cuando lo necesites.</p>}
      </section>

      <div ref={topRef} className="mt-10 scroll-mt-4">
        <DayStrip weeks={pack.weeks} days={pack.days} done={done} current={current} onSelect={go} />
      </div>

      <div className="mt-6 max-w-3xl mx-auto">
        <DayCard code={pack.code} day={day} totalDays={totalDays} video={dayVideo} done={done.has(current)} onToggle={toggle}
          onPrev={current > 1 ? () => go(current - 1) : null} onNext={current < totalDays ? () => go(current + 1) : null} />
      </div>

      {pack.partner_module && <PartnerModule content={pack.partner_module} code={pack.code} />}

      <SupportCard pack={pack} day={current} />

      <section className="mt-10">
        <p className="font-display text-2xl">Materiales del taller</p>
        <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {pack.planners.map((p) => (
            <Link key={p.id} to={`/pack/planificador/${p.id}`} data-testid={`planner-link-${p.id}`} className="bg-white rounded-lg border border-[var(--sand)] p-4 flex flex-col gap-2 transition-colors hover:border-[var(--forest)]">
              <Printer size={18} className="text-[var(--terra)]" /> <span className="font-semibold leading-tight">{p.title}</span>
            </Link>
          ))}
        </div>
      </section>

      <Celebrate day={party} nextDay={nextUndone} onNext={() => go(nextUndone)} onClose={() => setParty(null)} />
    </main>
  );
}
